<script>
  import { getContext, createEventDispatcher } from "svelte";
  import { Dimension, LinearDisplayFormat } from "dimtext";

  import Viewport from "#lib/drawing/ViewportFixed.svelte";
  import QuantityInput from "#lib/standard-inputs/QuantityInput.svelte";
  import liteDrawing from "@local/extensions/drawing/lite-drawing.js";
  import EditIcon from "@local/assets/icons/edit.svg";
  import PriceOverrideInput from "./PriceOverrideInput.svelte";
  import dimSettings from "@local/extensions/utilities/dim-settings.js";

  export let group;
  export let items;
  export let index = 1;
  export let typeIndex;
  export let jobProperties;
  export let job;
  export let sent;
  export let itemPrices;
  export let productList;
  export let fabrications = 0;

  const dispatch = createEventDispatcher();

  const selectedProducts = getContext("selectedProducts");

  $: du = $group.data.settings.display_unit;
  $: ds = dimSettings($group.data.settings);

  function formatThickness(val) {
    const sdf = "FRACTIONAL";

    if (!val) return "Thickness TBD";

    let v = val.format(LinearDisplayFormat[sdf], sdf === "DECIMAL" ? 3 : 5, {
      displayUnit: "inches",
    });

    if (v?.startsWith("0-")) {
      v = v.substring(2);
    }

    return `${v}"`;
  }

  function getItemFabrications(item) {
    if (!item.data.fabrications) return 0;

    const voids = item.data.fabrications.voids?.length || 0;
    const edge = item.data.fabrications.edge?.length || 0;
    const corner = item.data.fabrications.corner?.length || 0;
    const virtual =
      item.data.fabrications.virtual?.filter((f) => {
        if (f?.fabrication?.effect === "add") return false;
        return true;
      }).length || 0;

    return (voids + edge + corner + virtual) * item.quantity;
  }

  function getDimensions(item, ds) {
    if (!item) return null;
    if (item.width && item.height) {
      const fmt = {
        DECIMAL: LinearDisplayFormat.DECIMAL,
        FRACTIONAL: LinearDisplayFormat.FRACTIONAL,
        ARCHITECTURAL: LinearDisplayFormat.ARCHITECTURAL,
      }[ds.dimFormat];
      const unit = ds.displayUnit === "millimeters" ? "mm" : '"';
      const wdim = new Dimension(item.width);
      const hdim = new Dimension(item.height);

      const wtext = wdim.format(fmt, ds.dimPrecision, {
        displayUnit: ds.displayUnit,
        unicodeNumerals: true,
        unicodeSlash: true,
        fractionSeparator: false,
      });

      const htext = hdim.format(fmt, ds.dimPrecision, {
        displayUnit: ds.displayUnit,
        unicodeNumerals: true,
        unicodeSlash: true,
        fractionSeparator: false,
      });

      return `${wtext}${unit} x ${htext}${unit}`;
    }
  }

  function getArea(item) {
    return item.width && item.height ? (item.cache.width / 12) * (item.cache.height / 12) * item.quantity : 0;
  }

  function formattedArea(area, du) {
    return du === "millimeters" ? `${(area * 0.092903).toFixed(2)} m²` : `${area.toFixed(2)} sf`;
  }

  function undefinedItemText(u) {
    if (!u) return "";
    if (u === 1) return " (1 undefined item)";
    return ` (${u} undefined items)`;
  }

  function constraint(entry, prop) {
    const max = entry[`maximum_${prop}`];
    const min = entry[`minimum_${prop}`];
    const p = prop.replace("_", " ");
    if (max != null && min != null) {
      return `${min} ≤ ${p} < ${max}`;
    } else if (max != null) {
      return `${p} < ${max}`;
    } else if (min != null) {
      return `${min} ≤ ${p}`;
    }

    return null;
  }

  function getPrice(itemPrices, item) {
    if (item.price != null) return item.price;
    return itemPrices?.[item.id] || 0;
  }

  function formattedPriceEntry(entry) {
    if (!entry) return "";
    const constraints = ["area", "item_area", "item_quantity"]
      .map((prop) => constraint(entry, prop))
      .filter((c) => c);

    if (constraints.length) {
      return `(${constraints.join(", ")})`;
    } else {
      return "";
    }
  }

  function handleRowClick(event, item) {
    dispatch("click-item", { event, item });
  }
</script>

<tbody>
  {#each items as item, itemIndex}
    {@const size = getDimensions(item, ds)}
    {@const area = getArea(item)}
    {@const areaFormatted = formattedArea(area, du)}
    {@const quantity = item.quantity}
    {@const priceEach = getPrice(itemPrices, item)}
    {@const definedPrice = itemPrices?.[item.id] != null}
    {@const priceTotal = priceEach * quantity}
    {@const itemFabrications = getItemFabrications(item)}
    <tr
      class="item-row"
      class:selected={$selectedProducts[item.id]}
      on:click={(e) => handleRowClick(e, item)}>
      <td>
        {typeIndex + itemIndex + 1}
      </td>
      <td>
        <div class="w-full relative">
          <div class="font-bold">{item.description}</div>
          <div class="flex gap-2">
            <div class="w-24 h-24 relative flex-none">
              {#if item.width && item.height}
                <div class="bg-gray-100 w-full h-full">
                  <Viewport
                    padding={8}
                    drawing={liteDrawing(item, {
                      showMark: false,
                      showDims: false,
                      showOffsets: true,
                    })}
                    width={96}
                    height={96} />
                </div>
              {/if}
            </div>
            <div>
              <table class="type-info">
                {#if size}
                  <tr>
                    <td class="label">Size</td>
                    <td>
                      {size}
                    </td>
                  </tr>
                {/if}
                {#if size}
                  <tr>
                    <td class="label">Area</td>
                    <td>
                      {areaFormatted}
                    </td>
                  </tr>
                {/if}
                {#if item.notes}
                  <tr>
                    <td class="label">Notes</td>
                    <td>
                      {item.notes}
                    </td>
                  </tr>
                {/if}
              </table>
            </div>
          </div>
          {#if !sent}
            <button
              class="absolute right-0 top-0 text-gray-400 p-0.5 rounded hover:text-black hover:bg-gray-200"
              on:click={() => dispatch("edit-item", { id: item.id })}>
              <EditIcon />
            </button>
          {/if}
        </div>
      </td>
      <td class="numeric"> </td>
      <td class="numeric"> </td>
      {#if fabrications}
        <td class="numeric">
          <div class="aligned-text">
            {itemFabrications || ""}
          </div>
        </td>
      {/if}
      <td class="numeric flex gap-1 justify-end">
        <PriceOverrideInput
          {item}
          {productList}
          computedPrice={itemPrices?.[item.id]}
          {jobProperties}
          disabled={!productList?.allow_price_override || sent}
          on:input={(e) =>
            dispatch("update-item", {
              item,
              price_override: e.detail.value,
              price_override_basis: e.detail.basis,
            })} />
      </td>
      <td class="numeric">
        {#if !sent}
          <QuantityInput
            {quantity}
            on:input={(e) => dispatch("update-item", { quantity: e.detail.value, item })} />
        {:else}
          <div class="aligned-text">
            {quantity}
          </div>
        {/if}
      </td>
      <td class="numeric">
        <div class="aligned-text">
          {#if definedPrice}
            {priceTotal?.toFixed(2) || ""}
          {:else}
            <span class="text-red-500 font-bold">N/A</span>
          {/if}
        </div>
      </td>
    </tr>
  {/each}
</tbody>

<style lang="scss">
  tr.item-row {
    &.selected {
      @apply bg-blue-50;
      outline: 1px solid oklch(62.3% 0.214 259.815);
    }
  }

  td {
    @apply border-gray-300 align-top p-1;

    &.numeric {
      @apply text-right whitespace-nowrap;

      .aligned-text {
        @apply py-0.5;
      }
    }

    .button-icon {
      @apply text-gray-400 p-0.5 rounded;

      &:hover {
        @apply bg-gray-200 text-black;
      }
    }
  }

  table.type-info {
    td {
      padding: 0rem 0.25rem 0rem 0.25rem;
    }

    td.label {
      @apply text-gray-400 text-right;
    }
  }
</style>
